import { FC, useEffect, useState } from "react";
import Lottie from "lottie-react";

import { morphoUIStyles, font } from "../../configs";
import {
  capitalizeFirstLetter,
  handleAdditionalBrands,
  useIsMobile,
} from "../../utils";
import { GENDERS, getFileFromCloud } from "../../constants/modal";
import { findBrandByDomain } from "../../configs/configLoader";

import "./index.css";

interface IPropsBellyChoice {
  image: string | null;
  onClick: () => void;
  text: string;
  isSelected?: boolean;
  type?: string;
  gender?: string;
}

type BellyChoiceType = "unfocused" | "focused";

const BellyChoice: FC<IPropsBellyChoice> = ({
  image,
  onClick,
  text,
  isSelected,
  type,
  gender,
}) => {
  const [loading, setLoading] = useState(true);

  const [animationData, setAnimationData] = useState<any>(null);

  const isMobile = useIsMobile();

  const urlParams = new URLSearchParams(window.location.search);
  const lang = urlParams.get("lang")?.split("-")[0].toLowerCase();

  const brandDefined = findBrandByDomain();

  const isOgier = brandDefined?.name === "Ogier";
  const isKooples = brandDefined?.name === "The Kooples";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isGivenchy = brandDefined?.name === "Givenchy";

  const isCustomDomains = isVB || isGivenchy;

  const isGivenchyMobile = isGivenchy && isMobile;
  const isGivenchyDesktop = isGivenchy && !isMobile;

  const torsoClass = loading
    ? "torso_choice__image_loading"
    : "torso_choice__image";

  const cuissesClass = loading
    ? "cuisses_choice__image_loading"
    : "cuisses_choice__image";

  const getSelectorStyles = (selectorType: BellyChoiceType) => {
    const ctaStyles = morphoUIStyles.morphoCTA[selectorType]!;

    const selectorSizeStyles = {
      backgroundColor:
        ctaStyles.backgroundColor as React.CSSProperties["backgroundColor"],
      borderRadius:
        ctaStyles.borderRadius as React.CSSProperties["borderRadius"],
      border: `${
        ctaStyles.borderWidth as React.CSSProperties["borderWidth"]
      } solid ${ctaStyles.borderColor as React.CSSProperties["borderColor"]}`,
      fontWeight: ctaStyles.fontWeight as React.CSSProperties["fontWeight"],
      color: ctaStyles.fontColor as React.CSSProperties["color"],
      fontSize:
        (isMobile &&
          (lang === "de" || lang === "en") &&
          type === "torso" &&
          gender === GENDERS.F) ||
        (isMobile && isKooples)
          ? `calc(${ctaStyles.fontSize} - 2px)`
          : (ctaStyles.fontSize as React.CSSProperties["fontSize"]),
      textAlign: ctaStyles.textAlign as React.CSSProperties["textAlign"],
      textTransform:
        ctaStyles.textTransform as React.CSSProperties["textTransform"],
      borderWidthStyle: ctaStyles.borderWidth,
      letterSpacing: (ctaStyles as any)?.letterSpacing,
    };

    return selectorSizeStyles;
  };

  const selectorStyles = isSelected
    ? getSelectorStyles("focused")
    : getSelectorStyles("unfocused");

  const isAdditionalBrands = handleAdditionalBrands(brandDefined?.name);

  const getTextTransform = (textTransform?: string) => {
    return textTransform === "capitalize" ? "none" : textTransform;
  };

  const getGap = (loading?: boolean) => {
    return isGivenchy ? "20px" : loading ? 0 : "15px";
  };

  const getBorder = (isAdditionalBrands?: boolean, fallbackBorder?: string) => {
    if (isAdditionalBrands && isCustomDomains) return "none";
    return fallbackBorder;
  };

  const getHeight = (isAdditionalBrands?: boolean) => {
    if (isGivenchyMobile) return "155px";
    if (isGivenchyDesktop) return "220px";
    if (isAdditionalBrands && isCustomDomains) return "auto";
    if (isAdditionalBrands && !isCustomDomains) return "240px";
    return "220px";
  };

  const getMinHeight = () => {
    if (isGivenchyMobile) return "155px";
    return "177px";
  };

  useEffect(() => {
    fetch(getFileFromCloud("assets/animations/loading_image.json"))
      .then((res) => res.json())
      .then(setAnimationData)
      .catch(console.error);
  }, []);

  return (
    <button
      className={isSelected ? "belly_choice_selected" : "belly_choice"}
      onClick={onClick}
      style={{
        ...selectorStyles,
        fontFamily: `${font}, sans-serif !important`,
        textTransform: getTextTransform(
          selectorStyles?.textTransform,
        ) as React.CSSProperties["textTransform"],
        gap: getGap(loading),
        border: getBorder(isAdditionalBrands, selectorStyles.border),
        height: getHeight(isAdditionalBrands),
        minHeight: getMinHeight(),
      }}
    >
      {loading === true ? (
        <Lottie
          className="loading"
          animationData={animationData}
          width={90}
          height={120}
        />
      ) : null}
      <div
        className="belly-data-container"
        style={{
          gap: isGivenchy ? 0 : "15px",
        }}
      >
        <img
          className={
            type === "torso"
              ? torsoClass
              : type === "cuisses"
                ? cuissesClass
                : loading
                  ? "belly_choice__image_loading"
                  : "belly_choice__image"
          }
          style={{
            width: isGivenchy ? "auto" : isAdditionalBrands ? "110px" : "90px",
            maxWidth: isGivenchy
              ? "150px"
              : isAdditionalBrands
                ? "140px"
                : "120px",
            borderRadius: isAdditionalBrands
              ? "0"
              : selectorStyles.borderRadius,
            height: isAdditionalBrands
              ? "auto"
              : isGivenchyDesktop
                ? "180px"
                : "120px",
            transform: isOgier
              ? isMobile
                ? "scale(1.4)"
                : "scale(1.2)"
              : undefined,
            pointerEvents: "none",
            willChange: "transform",
            objectFit: isGivenchy ? "cover" : "initial",
            marginTop: isGivenchy ? "15px" : 0,
          }}
          alt={type}
          src={image || undefined}
          onLoad={() => setLoading(false)}
        />

        <span
          style={{
            ...selectorStyles,
            border: "none",
            fontFamily: `${font}, sans-serif !important`,
            textTransform:
              selectorStyles?.textTransform === "capitalize"
                ? "none"
                : selectorStyles?.textTransform,
            backgroundColor: "transparent",
            position: "relative",
            top: isGivenchyMobile
              ? "30px"
              : isGivenchyDesktop
                ? "40px"
                : isOgier && isMobile
                  ? "20px"
                  : 0,
            textDecoration: isSelected && isGivenchy ? "underline" : "none",
          }}
        >
          {selectorStyles?.textTransform === "capitalize"
            ? capitalizeFirstLetter(text)
            : text}
        </span>
      </div>
    </button>
  );
};

export default BellyChoice;
